import { Component, OnInit, Input, inject } from '@angular/core';
import { ReportingService } from '../../reporting.service';
import { AdminService } from '../../../admin/admin.service';
import { Application, Unit } from '../../../models/admin-models';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
  GridComponent,
  GridModule,
  ExcelModule,
} from '@progress/kendo-angular-grid';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { PercentPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-rep-exceptions-report',
  templateUrl: './rep-exceptions.component.html',
  styleUrls: ['./rep-exceptions.component.scss'],
  imports: [
    MatSelectModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatIconModule,
    GridModule,
    ExcelModule,
    FormsModule,
    PercentPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
  ],
})
export class RepExceptionsComponent implements OnInit {
  private rptSvc = inject(ReportingService);
  private admnSrvc = inject(AdminService);
  private ss = inject(SessionStorageService);
  dialog = inject(MatDialog);

  @Input() alertIds = [];
  @Input() ignoreUnitValidation = false;
  exceptionsData = { data: null, headers: [] };
  startDate;
  endDate;
  alertId;
  filteredUnits = [];
  unitsControl = new FormControl();
  units: Unit[] = [] as Unit[];
  selectedUnit;
  applications: Application[] = [];
  selectedApp;
  viewAlertIds = true;

  ngOnInit(): void {
    this.getUnits();
    this.getDropdownOptions();
  }

  getDropdownOptions() {
    const globals = this.ss.get('globals');
    if (globals) {
      globals.Modules.forEach(app => {
        switch (app.toLowerCase()) {
          case 'aw':
            if (this.hasAlerts(app.toLowerCase())) {
              this.applications.push({
                name: 'Annuity Wizard',
                value: app.toLowerCase(),
              });
            }
            break;
          case 'iw':
            if (this.hasAlerts(app.toLowerCase())) {
              this.applications.push({
                name: 'Investment Wizard',
                value: app.toLowerCase(),
              });
            }
            break;
          case 'li':
            if (this.hasAlerts(app.toLowerCase())) {
              this.applications.push({
                name: 'Life Insurance Wizard',
                value: app.toLowerCase(),
              });
            }
            break;
          case 'pp':
            if (this.hasAlerts(app.toLowerCase())) {
              this.applications.push({
                name: 'Product Profiler',
                value: app.toLowerCase(),
              });
            }
            break;
          case 'se':
            if (this.hasAlerts(app.toLowerCase())) {
              this.applications.push({
                name: 'Suggestion Engine',
                value: app.toLowerCase(),
              });
            }
            break;
          default:
            break;
        }
      });
    }
  }

  getUnits() {
    this.unitsControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value) {
          this.admnSrvc.getUnitList(value, true).subscribe(resp => {
            if (resp['names']) {
              this.filteredUnits = resp['names'];
            }
          });
        }
      });
  }

  checkSelection() {
    if (!this.ignoreUnitValidation) {
      if (
        !this.filteredUnits ||
        !this.filteredUnits.find(unit => {
          return unit.id === this.unitsControl.value;
        })
      ) {
        this.unitsControl.setValue(null);
      }
    }
  }

  autoCompleteUnitChanged(ev) {
    this.unitsControl.setValue(ev.option.value);
  }

  getExceptions() {
    this.exceptionsData.data = [];
    this.exceptionsData.headers = [];
    const start = new Date(this.startDate).toISOString();
    const end = new Date(this.endDate).toISOString();
    this.viewAlertIds = false;
    this.rptSvc
      .getRepExceptions(
        start,
        end,
        this.alertId,
        this.selectedApp,
        this.selectedUnit
      )
      .subscribe({
        next: data => {
          if (data.results.data.length > 0) {
            this.exceptionsData.data = [...data.results.data];
            const keys = Object.keys(data.results.data[0]);
            keys.forEach(x => {
              this.exceptionsData.headers.push({ DataField: x, Label: x });
            });
          }
        },
        error: error => {
          console.error(error);
        },
      });
  }

  exportToExcel(grid: GridComponent): void {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        grid.saveAsExcel();
      }
      dialogRef = null;
    });
  }

  hasAlerts(app = this.selectedApp) {
    return this.alertIds.find(alertId => alertId.Module.toLowerCase() === app);
  }
}
